import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { GameProvider } from './context/GameContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { FontSizeProvider } from './context/FontSizeContext';
import { FontSizeModal } from './components/common/FontSizeModal';
import { QRCodeProvider } from './context/QRCodeContext';
import { QRCodeModal } from './components/common/QRCodeModal';
import PWABlocker from './components/common/PWABlocker';

import Home from './pages/Home';
import Profile from './pages/Profile';
import LocalGame from './pages/LocalGame';
import MultiplayerLobby from './pages/MultiplayerLobby';
import MultiplayerGame from './pages/MultiplayerGame';
import Training from './pages/Training';
import TrainingGame from './pages/TrainingGame';
import Study from './pages/Study';
import StudyFlashcards from './pages/StudyFlashcards';
import StudyDialogue from './pages/StudyDialogue';
import PhraseManager from './pages/PhraseManager';
import Teams from './pages/Teams';
import TeamDetail from './pages/TeamDetail';
import Settings from './pages/Settings';
import Admin from './pages/Admin';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';

// Protected: requires login
const Protected = ({ children }) => {
  const { currentUser } = useAuth();
  if (!currentUser) return <Navigate to="/?login=true" replace />;
  return children;
};

// Admin Protected: requires login AND admin status
const AdminProtected = ({ children }) => {
  const { currentUser, isAdmin } = useAuth();
  if (!currentUser) return <Navigate to="/?login=true" replace />;
  if (!isAdmin) return <Navigate to="/" replace />;
  return children;
};

// Fix for mobile orientation change layout bugs
const OrientationHandler = ({ children }) => {
  const [orientation, setOrientation] = React.useState(
    window.innerHeight > window.innerWidth ? 'portrait' : 'landscape',
  );

  React.useEffect(() => {
    const mq = window.matchMedia('(orientation: portrait)');
    const handleUpdate = () => {
      const newOrientation = mq.matches ? 'portrait' : 'landscape';
      setOrientation(newOrientation);
      document.documentElement.setAttribute('data-orientation', newOrientation);
    };

    mq.addEventListener('change', handleUpdate);
    return () => mq.removeEventListener('change', handleUpdate);
  }, []);

  return (
    <div className="app-container" key={orientation}>
      {children}
    </div>
  );
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <FontSizeProvider>
          <QRCodeProvider>
            <BrowserRouter>
              {/* Portrait mode blocker */}
              <div id="landscape-overlay">
                <span className="rotate-icon">📱</span>
                <h1>Ruota il dispositivo</h1>
                <p>Chain Reaction funziona solo in orizzontale!</p>
              </div>

              <FontSizeModal />
              <QRCodeModal />
              <PWABlocker />
              <OrientationHandler>
                <Routes>
                  {/* Public */}
                  <Route
                    path="/local"
                    element={
                      <GameProvider>
                        <LocalGame />
                      </GameProvider>
                    }
                  />
                  <Route
                    path="/multiplayer"
                    element={
                      <Protected>
                        <MultiplayerLobby />
                      </Protected>
                    }
                  />
                  <Route
                    path="/multiplayer/game"
                    element={
                      <Protected>
                        <MultiplayerGame />
                      </Protected>
                    }
                  />

                  {/* Home (accessible logged out too) */}
                  <Route path="/" element={<Home />} />

                  {/* Protected */}
                  <Route
                    path="/profile"
                    element={
                      <Protected>
                        <Profile />
                      </Protected>
                    }
                  />
                  <Route
                    path="/training"
                    element={
                      <Protected>
                        <Training />
                      </Protected>
                    }
                  />
                  <Route
                    path="/training/game"
                    element={
                      <Protected>
                        <TrainingGame />
                      </Protected>
                    }
                  />
                  <Route
                    path="/study"
                    element={
                      <Protected>
                        <Study />
                      </Protected>
                    }
                  />
                  <Route
                    path="/study/flashcards"
                    element={
                      <Protected>
                        <StudyFlashcards />
                      </Protected>
                    }
                  />
                  <Route
                    path="/study/dialogue"
                    element={
                      <Protected>
                        <StudyDialogue />
                      </Protected>
                    }
                  />
                  <Route
                    path="/phrases"
                    element={
                      <Protected>
                        <PhraseManager />
                      </Protected>
                    }
                  />
                  <Route
                    path="/teams"
                    element={
                      <Protected>
                        <Teams />
                      </Protected>
                    }
                  />
                  <Route
                    path="/teams/:id"
                    element={
                      <Protected>
                        <TeamDetail />
                      </Protected>
                    }
                  />
                  <Route
                    path="/settings"
                    element={
                      <Protected>
                        <Settings />
                      </Protected>
                    }
                  />

                  {/* Hidden admin */}
                  <Route
                    path="/admin-console"
                    element={
                      <AdminProtected>
                        <Admin />
                      </AdminProtected>
                    }
                  />

                  {/* Legal — public, no auth required */}
                  <Route path="/privacy" element={<PrivacyPolicy />} />
                  <Route path="/terms" element={<TermsOfService />} />

                  {/* Fallback */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </OrientationHandler>
            </BrowserRouter>
          </QRCodeProvider>
        </FontSizeProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
